import React from "react";
import {
  Alert,
  Button,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CenterScreen from "../../../components/wrappers/CenterScreen";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { LOGIN } from "../../../enums/UrlPaths";
import authApi from "../../../utils/api/authApi";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import { zodResolver } from "@hookform/resolvers/zod";
import { passwordMatcher } from "../utils/zodPasswordMatcher";
import {
  EMPLOYEE_ACTIVATION,
  EMPLOYEE_ACTIVATION_INFO,
} from "../../../enums/ApiPaths";
import { TEXT_FIELDS } from "../enums/EmployeeActivationFields";
import {
  getBearerToken,
  getTokenFromUrl,
  logout,
} from "../../../utils/Functions";
import { SnackbarContext } from "../../../contexts/Snackbar";

const ELSE_WIDTH = "40%";
const STATUS = {
  LOADING: "loading",
  READY: "ready",
  INVALID: "invalid",
  ACTIVATED: "activated",
};

export default function EmployeeActivationPage() {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      Password: "",
      ConfirmPassword: "",
    },
    resolver: zodResolver(passwordMatcher(t)),
  });

  const navigate = useNavigate();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const token = getTokenFromUrl();
  const [status, setStatus] = React.useState(
    token ? STATUS.LOADING : STATUS.INVALID
  );
  const [email, setEmail] = React.useState("");

  React.useEffect(() => {
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    authApi
      .get(EMPLOYEE_ACTIVATION_INFO + encodeURIComponent(token))
      .then((res) => {
        if (!cancelled) {
          setEmail(res.data.email);
          setStatus(STATUS.READY);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus(STATUS.INVALID);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submitData = async () => {
    const obj = watch();
    try {
      await authApi.post(EMPLOYEE_ACTIVATION + encodeURIComponent(token), obj);
      const bearerToken = await getBearerToken();
      if (bearerToken) {
        logout();
      }
      setStatus(STATUS.ACTIVATED);
    } catch (error) {
      handleSnackbar(error?.response?.data, snackbarSeverity.ERROR);
    }
  };

  if (status === STATUS.LOADING) {
    return <LinearProgress />;
  }

  return (
    <CenterScreen>
      <Stack
        sx={{
          width: {
            xs: "90%",
            sm: "90%",
            md: ELSE_WIDTH,
            lg: ELSE_WIDTH,
            xl: ELSE_WIDTH,
          },
        }}
        spacing={2}
      >
        {status === STATUS.INVALID && (
          <Alert variant="filled" severity="error">
            {t("InvalidOrExpiredLink")}
          </Alert>
        )}

        {status === STATUS.ACTIVATED && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Alert severity="success">{t("AccountActivated")}</Alert>
              <Button
                variant="contained"
                color="info"
                onClick={() => navigate(LOGIN)}
              >
                {t("GoToLogin")}
              </Button>
            </Stack>
          </Paper>
        )}

        {status === STATUS.READY && (
          <>
            <Alert variant="filled" severity="info">
              {t("AccountActivationInformation")}
            </Alert>
            <Paper sx={{ p: 3 }}>
              <Stack mb={2}>
                <Typography variant="h4">{t("AccountActivation")}</Typography>
              </Stack>
              <form onSubmit={handleSubmit(submitData)}>
                <Stack spacing={2} width={"100%"}>
                  <TextField label={t("Email")} value={email} disabled />
                  {TEXT_FIELDS.map((field) => (
                    <TextField
                      key={field.name}
                      label={t(field.label)}
                      variant={field.variant}
                      required
                      {...register(field.name)}
                      error={!!errors?.[field.name]}
                      helperText={errors?.[field.name]?.message || ""}
                      type={"password"}
                    />
                  ))}
                  <Button type="submit" variant="contained" color="info">
                    {t("ActivateAccount")}
                  </Button>
                </Stack>
              </form>
            </Paper>
          </>
        )}
      </Stack>
    </CenterScreen>
  );
}